import { useState, useEffect } from 'react'
import { RowItem } from '@/components/composites'
import { useConversionScopedStore } from '@/store/scoped/useConversionScopedStore'
import { useCalculateConversionFee } from '@/domain/transfer/useCases/useCalculateConversionFee'
import { currencyUtils } from '@/utils/currency'
import { useDebounceCallBack } from '@/hooks'
import type { FiatCode } from '@/constants/currencyCodes'

export const MINIMUM_FIAT_AMOUNT_TO_CALCULATE_FEE = 1.01

const { removeTrailingZeros } = currencyUtils

export const EstimatedTransactionFee = () => {
  const [fee, setFee] = useState('0')

  const { from: debitData, to: creditData } = useConversionScopedStore(
    (state) => state.conversion,
  )
  const setConversionPayload = useConversionScopedStore(
    (state) => state.setConversionPayload,
  )
  const setLastFeePayload = useConversionScopedStore(
    (state) => state.setLastFeePayload,
  )
  const setConversion = useConversionScopedStore((state) => state.setConversion)
  const setHasErrorOnFeeCalculation = useConversionScopedStore(
    (state) => state.setHasErrorOnFeeCalculation,
  )

  const thereIsLessThanMin =
    currencyUtils.parseAmountToFloat({
      amount: debitData.fiatAmount?.amount || '0',
      decimalSeparator: '.',
    }) < MINIMUM_FIAT_AMOUNT_TO_CALCULATE_FEE

  const { calculateConversionFee, isPending: isCalculatingFee } =
    useCalculateConversionFee({
      onSuccess: ({ fee_amount, credit_amount, total_debit_amount }) => {
        setHasErrorOnFeeCalculation(false)
        setFee(fee_amount.amount)
        setConversionPayload({
          convert_type: 'convert',
          fee_amount: fee_amount.amount,
          credit_amount: credit_amount?.amount,
          credit_currency:
            credit_amount?.currency || creditData.cryptoAmount.currency,
          debit_amount: total_debit_amount.amount,
          debit_currency:
            total_debit_amount?.currency || debitData.cryptoAmount.currency,
        })

        setConversion({
          to: {
            currencyInfoInWallet: creditData.currencyInfoInWallet,
            fiatAmount: {
              amount: credit_amount.fiat_amount,
              currency: credit_amount.fiat_currency as FiatCode,
            },
            cryptoAmount: {
              amount: credit_amount.amount,
              currency: credit_amount.currency,
            },
          },
        })
      },
      onError: () => {
        setHasErrorOnFeeCalculation(true)
        setFee('0')
      },
    })

  const debitCurrencyCode =
    debitData?.currencyInfoInWallet?.displayCurrencyCode || ''

  const triggerFeeCalculation = async () => {
    const feePayload = {
      credit_currency: creditData.cryptoAmount.currency,
      debit_amount: debitData.cryptoAmount.amount,
      debit_currency: debitData.cryptoAmount.currency,
    }

    setLastFeePayload(feePayload)

    await calculateConversionFee(feePayload)
  }

  const debounceOnChangeConversionInputs = useDebounceCallBack(() => {
    // IF THE VALUE IS LESS THAN 1.01 USD, SET FEE 0, BECAUSE THE API DOES NOT ACCEPT VALUES LESS THAN 1.01 USD
    if (thereIsLessThanMin) {
      setFee('0')
      return
    }

    triggerFeeCalculation()
  }, 1000)

  useEffect(() => {
    debounceOnChangeConversionInputs()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    debitData.currencyInfoInWallet.code,
    debitData.cryptoAmount.amount,
    creditData.currencyInfoInWallet.code,
  ])

  return (
    <RowItem
      label="transactions:rowItemLabels.transactionFee"
      value={`${removeTrailingZeros(fee)} ${debitCurrencyCode}`}
      skeletonActive={isCalculatingFee}
    />
  )
}

// Export hook to check if calculating fee
export const useIsCalculatingFee = () => {
  const { from: debitData } = useConversionScopedStore(
    (state) => state.conversion,
  )

  const thereIsLessThanMin =
    currencyUtils.parseAmountToFloat({
      amount: debitData.fiatAmount?.amount || '0',
      decimalSeparator: '.',
    }) < MINIMUM_FIAT_AMOUNT_TO_CALCULATE_FEE

  const { isPending } = useCalculateConversionFee()

  return !thereIsLessThanMin && isPending
}
